import { useEffect, useMemo, useState } from 'react';
import styles from './Meter.module.css';

// One definition of the dashboard's progress bar. Every panel that shows a
// proportion uses this, so a bar means the same thing and behaves the same way
// everywhere: it starts empty, fills with a short spring, and carries a slow
// highlight while it is on screen.
//
//   <Meter value={35} total={132} tone="accent" />
//   <Meter pct={64} tone="brand" size="lg" delay={80} />

const clampPct = (n) => (Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0);

// Bars start collapsed and are armed a frame later so the browser has a zero
// width to transition *from*. Without this the fill is simply painted at its
// final size and the motion never happens.
//
// Deliberately mount-only: once a bar has filled, a refresh that moves its
// value should travel from where it was, not collapse and grow again.
const useArmed = () => {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setArmed(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, []);

  return armed;
};

const Meter = ({
  value,
  total,
  pct,
  tone = 'accent',
  size = 'md',
  delay = 0,
  sheen = true,
  // A bar at 0 still deserves a shape: the track shows a faint dormant stub
  // rather than reading as a rendering failure.
  dormant = true,
  className = '',
  label,
}) => {
  const target = useMemo(() => {
    if (Number.isFinite(pct)) return clampPct(pct);
    const t = Number(total) || 0;
    return t > 0 ? clampPct(((Number(value) || 0) / t) * 100) : 0;
  }, [pct, value, total]);

  const armed = useArmed();
  const empty = target <= 0;

  return (
    <span
      className={`${styles.track} ${styles[size]} ${empty && dormant ? styles.trackEmpty : ''} ${className}`}
      // A labelled meter is announced as a progress bar; an unlabelled one is
      // decoration beside a figure that already states the number.
      role={label ? 'progressbar' : undefined}
      aria-label={label}
      aria-valuemin={label ? 0 : undefined}
      aria-valuemax={label ? 100 : undefined}
      aria-valuenow={label ? Math.round(target) : undefined}
      aria-hidden={label ? undefined : true}
    >
      <span
        className={`${styles.fill} ${styles[tone]} ${empty ? '' : styles.fillOn} ${
          sheen ? styles.sheen : ''
        }`}
        style={{
          width: armed ? `${target}%` : '0%',
          transitionDelay: `${delay}ms`,
          animationDelay: `${delay + 260}ms`,
        }}
      />
    </span>
  );
};

// A single track carrying several tones end to end — the shape of a
// distribution rather than the size of one number. Segments grow in reading
// order so the bar assembles itself instead of appearing whole.
export const SegmentedMeter = ({
  segments = [],
  size = 'lg',
  stagger = 90,
  className = '',
  onSegmentEnter,
  activeKey,
}) => {
  const armed = useArmed();
  const hoverable = typeof onSegmentEnter === 'function';

  return (
    <span
      className={`${styles.track} ${styles[size]} ${styles.segTrack} ${className}`}
      onMouseLeave={hoverable ? () => onSegmentEnter(null) : undefined}
    >
      {segments.map((s, i) => (
        <span
          key={s.key}
          className={`${styles.seg} ${styles[s.tone || 'accent']} ${styles.sheen} ${
            activeKey && activeKey !== s.key ? styles.segDim : ''
          }`}
          style={{
            width: armed ? `${clampPct(s.pct)}%` : '0%',
            transitionDelay: `${i * stagger}ms`,
            animationDelay: `${i * stagger + 320}ms`,
          }}
          title={s.title}
          onMouseEnter={hoverable ? () => onSegmentEnter(s.key) : undefined}
        />
      ))}
    </span>
  );
};

export default Meter;
